import { useState } from 'react'
import { Card, Button, Table, Form, Row, Col, Spinner, Alert, Accordion } from 'react-bootstrap'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import { useAuth } from '../../hooks/useAuth'
import { useSyncedLocalClientId } from '../../hooks/useSyncedLocalClientId'
import api from '../../services/api'
import { formatCurrency } from '../../utils/formatters'

const getClientName = (client) => {
  if (!client) return 'N/A'
  if (client.razon_social) return client.razon_social
  if (client.business_name) return client.business_name
  const parts = []
  if (client.nombre) parts.push(client.nombre)
  if (client.apellido_paterno) parts.push(client.apellido_paterno)
  if (parts.length > 0) return parts.join(' ')
  return client.name || 'N/A'
}

const TaxCalculationDashboard = () => {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const { clientId, setClientId, selectDisabled } = useSyncedLocalClientId()
  const [fiscalPeriodId, setFiscalPeriodId] = useState('')

  const { data: clients } = useQuery(
    'clients-for-tax-calculation',
    async () => {
      const response = await api.get('/clients/clients/?activo=true')
      return response.data.results || response.data || []
    },
    { enabled: !!user }
  )

  const { data: fiscalPeriods } = useQuery(
    'fiscal-periods-for-tax-calculation',
    async () => {
      const response = await api.get('/accounting/fiscal-periods/')
      return response.data.results || response.data || []
    },
    { enabled: !!user }
  )

  const { data: calculations, isLoading: loadingCalculations } = useQuery(
    ['tax-calculations', clientId, fiscalPeriodId],
    async () => {
      if (!clientId || !fiscalPeriodId) return []
      const params = new URLSearchParams({ client_id: clientId, fiscal_period_id: fiscalPeriodId })
      const response = await api.get(`/accounting/tax-calculations/?${params.toString()}`)
      return response.data.results || response.data || []
    },
    { enabled: !!user && !!clientId && !!fiscalPeriodId }
  )

  const { data: breakdown, isLoading: loadingBreakdown } = useQuery(
    ['tax-breakdown', clientId, fiscalPeriodId],
    async () => {
      if (!clientId || !fiscalPeriodId) return null
      const params = new URLSearchParams({ client_id: clientId, fiscal_period_id: fiscalPeriodId })
      const response = await api.get(`/accounting/tax-calculations/breakdown/?${params.toString()}`)
      return response.data
    },
    { enabled: !!user && !!clientId && !!fiscalPeriodId }
  )

  const calculateMutation = useMutation(
    async () => {
      const response = await api.post('/accounting/tax-calculations/calculate/', {
        client_id: Number(clientId),
        fiscal_period_id: Number(fiscalPeriodId)
      })
      return response.data
    },
    {
      onSuccess: (_data) => {
        toast.success('Impuestos calculados correctamente')
        queryClient.invalidateQueries(['tax-calculations', clientId, fiscalPeriodId])
      },
      onError: (error) => {
        const msg = error.response?.data?.error || 'Error al calcular impuestos'
        toast.error(msg)
      }
    }
  )

  const handleCalculate = () => {
    if (!clientId || !fiscalPeriodId) {
      toast.warning('Selecciona cliente y período fiscal')
      return
    }
    calculateMutation.mutate()
  }

  const sortedPeriods = fiscalPeriods
    ? [...fiscalPeriods].sort((a, b) => {
        if (a.year !== b.year) return b.year - a.year
        return b.month - a.month
      })
    : []

  const isrCalc = calculations?.find((c) => c.tax_type === 'ISR')
  const ivaCalc = calculations?.find((c) => c.tax_type === 'IVA')
  const iepsCalc = calculations?.find((c) => c.tax_type === 'IEPS')

  return (
    <div className="tax-calculation-dashboard">
      <Card className="mb-4">
        <Card.Header>
          <h5 className="mb-0">Cálculo de Impuestos</h5>
        </Card.Header>
        <Card.Body>
          {fiscalPeriods && fiscalPeriods.length === 0 && (
            <Alert variant="warning" className="mb-3">
              No hay períodos fiscales. Ve a <strong>Períodos Fiscales</strong> para crear al menos uno,
              o ejecuta en backend: <code>python manage.py seed_fiscal_periods</code>
            </Alert>
          )}
          <Row className="g-3 mb-3">
            <Col md={4}>
              <Form.Label>Cliente</Form.Label>
              <Form.Select
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                disabled={selectDisabled}
              >
                <option value="">Seleccionar cliente</option>
                {(clients || []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {getClientName(c)}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={4}>
              <Form.Label>Período fiscal</Form.Label>
              <Form.Select
                value={fiscalPeriodId}
                onChange={(e) => setFiscalPeriodId(e.target.value)}
              >
                <option value="">Seleccionar período</option>
                {(sortedPeriods || []).map((fp) => (
                  <option key={fp.id} value={fp.id}>
                    {fp.year}-{String(fp.month).padStart(2, '0')}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={4} className="d-flex align-items-end">
              <Button
                variant="primary"
                onClick={handleCalculate}
                disabled={!clientId || !fiscalPeriodId || calculateMutation.isLoading}
              >
                {calculateMutation.isLoading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Calculando...
                  </>
                ) : (
                  'Calcular impuestos'
                )}
              </Button>
            </Col>
          </Row>

          {loadingCalculations && clientId && fiscalPeriodId && (
            <Alert variant="info" className="mb-0">
              <Spinner animation="border" size="sm" className="me-2" />
              Cargando cálculos...
            </Alert>
          )}

          {!loadingCalculations && calculations?.length > 0 && (
            <Card className="mb-0">
              <Card.Header>Resultados</Card.Header>
              <Card.Body>
                <Table striped bordered hover size="sm">
                  <thead>
                    <tr>
                      <th>Impuesto</th>
                      <th className="text-end">Base</th>
                      <th className="text-end">Tasa %</th>
                      <th className="text-end">Monto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isrCalc && (
                      <tr>
                        <td>ISR</td>
                        <td className="text-end">{formatCurrency(isrCalc.base_amount)}</td>
                        <td className="text-end">{Number(isrCalc.tax_rate)}</td>
                        <td className="text-end">{formatCurrency(isrCalc.tax_amount)}</td>
                      </tr>
                    )}
                    {ivaCalc && (
                      <tr>
                        <td>IVA</td>
                        <td className="text-end">{formatCurrency(ivaCalc.base_amount)}</td>
                        <td className="text-end">{Number(ivaCalc.tax_rate)}</td>
                        <td className="text-end">{formatCurrency(ivaCalc.tax_amount)}</td>
                      </tr>
                    )}
                    {iepsCalc && (
                      <tr>
                        <td>IEPS</td>
                        <td className="text-end">{formatCurrency(iepsCalc.base_amount)}</td>
                        <td className="text-end">{Number(iepsCalc.tax_rate)}</td>
                        <td className="text-end">{formatCurrency(iepsCalc.tax_amount)}</td>
                      </tr>
                    )}
                  </tbody>
                </Table>
                <small className="text-muted">
                  ISR: base desde contabilidad (ingresos − deducciones). IVA e IEPS: cobrado − pagado desde CFDIs.
                </small>

                {breakdown && (breakdown.income_transactions?.length > 0 || breakdown.deductible_transactions?.length > 0) && (
                  <Accordion className="mt-3">
                    <Accordion.Item eventKey="0">
                      <Accordion.Header>Desglose de transacciones (auditoría)</Accordion.Header>
                      <Accordion.Body>
                        {loadingBreakdown ? (
                          <Spinner animation="border" size="sm" />
                        ) : (
                          <>
                            {breakdown.income_transactions?.length > 0 && (
                              <div className="mb-3">
                                <h6>Ingresos ({breakdown.income_transactions.length} transacciones)</h6>
                                <Table size="sm" striped>
                                  <thead>
                                    <tr>
                                      <th>Fecha</th>
                                      <th>Referencia</th>
                                      <th>Cuenta</th>
                                      <th className="text-end">Crédito</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {breakdown.income_transactions.map((t) => (
                                      <tr key={t.id}>
                                        <td>{t.date}</td>
                                        <td>{t.reference}</td>
                                        <td>{t.account__code} {t.account__name}</td>
                                        <td className="text-end">{formatCurrency(t.credit)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </Table>
                              </div>
                            )}
                            {breakdown.deductible_transactions?.length > 0 && (
                              <div>
                                <h6>Deducciones ({breakdown.deductible_transactions.length} transacciones)</h6>
                                <Table size="sm" striped>
                                  <thead>
                                    <tr>
                                      <th>Fecha</th>
                                      <th>Referencia</th>
                                      <th>Cuenta</th>
                                      <th>Regla</th>
                                      <th className="text-end">Débito</th>
                                      <th className="text-end">Deducible</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {breakdown.deductible_transactions.map((t) => (
                                      <tr key={t.id}>
                                        <td>{t.date}</td>
                                        <td>{t.reference}</td>
                                        <td>{t.account_code} {t.account_name}</td>
                                        <td>{t.regla_nombre || '-'}</td>
                                        <td className="text-end">{formatCurrency(t.debit)}</td>
                                        <td className="text-end">{formatCurrency(t.monto_deducible)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </Table>
                              </div>
                            )}
                          </>
                        )}
                      </Accordion.Body>
                    </Accordion.Item>
                  </Accordion>
                )}
              </Card.Body>
            </Card>
          )}

          {!loadingCalculations && clientId && fiscalPeriodId && calculations?.length === 0 && (
            <Alert variant="secondary">
              No hay cálculos para este cliente y período. Haz clic en &quot;Calcular impuestos&quot; para generarlos.
            </Alert>
          )}
        </Card.Body>
      </Card>
    </div>
  )
}

export default TaxCalculationDashboard
